import { OnInit, OnDestroy, EventEmitter, Output, Input, Directive } from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  Pagination, ApiVisa } from 'blg-akaun-ts-lib';
import { ReplaySubject } from 'rxjs';
import { debounceTime, tap, filter, switchMap, distinctUntilChanged, map } from 'rxjs/operators';
import { SubSink } from 'subsink2';
import { IDropDownOptions } from '../models/drop-down-option.model';
import { AppConfig } from '../shared-utilities/visa';

@Directive({})
export abstract class DropDownComponent<T> implements OnInit, OnDestroy {
  
  apiVisa = AppConfig.apiVisa;
  @Input() required: boolean;
  @Input() label: string;
  @Input() entity: FormControl;
  
  @Output() entityChange = new EventEmitter<FormControl>();
  @Output() entitySelected = new EventEmitter<T>();

  protected subs = new SubSink();
  
  options: IDropDownOptions<T>;
  serverSideFiltering = new FormControl();
  serverSideSearching = false;
  filteredOptions$: ReplaySubject<T[]> = new ReplaySubject<T[]>(1);
  entityList: T[] = [];
  selectedEntity: T;

  constructor() {
  }

  ngOnInit() {
    if (this.entity.value) {
      this.subs.sink = this.options.service.getByGuid(this.entity.value, this.apiVisa).subscribe( resolved => {
        this.entityList = [resolved.data];
        this.filteredOptions$.next([resolved.data]);
        this.onSelect(this.options.guidSelector(resolved.data));
      });
    }
    this.subs.sink = this.serverSideFiltering.valueChanges.pipe(
        filter(searchText => searchText.length > 3),
        tap(() => this.serverSideSearching = true),
        debounceTime(300),
        distinctUntilChanged(),
        switchMap(searchText => this.searchEntity(searchText))
      ).subscribe( resolve => {
        const resolved = resolve.filter(x => x);
        this.serverSideSearching = false;
        if (this.selectedEntity) {
          this.filteredOptions$.next([...resolved, this.selectedEntity]);
          this.entityList = [...resolved, this.selectedEntity];
        } else {
          this.filteredOptions$.next(resolved);
          this.entityList = resolved;
        }
      }, error => {
        this.serverSideSearching = false;
        console.error(error);
      });
  }

  /**
   * Server-side typeahead for the drop-down.
   *
   * Replaces the SubQueryService round-trip (raw SELECT -> list of guids -> one
   * getByCriteria per guid) with a single criteria-based getByCriteria. The
   * concrete options object supplies the criteria via `searchCriteria`, which
   * carries both the keyword and whatever scoping the old SELECT's WHERE clause
   * had.
   */
  searchEntity(searchText: string) {
    return this.options.service.getByCriteria(
      new Pagination(0, 100, this.options.searchCriteria(searchText)),
      this.apiVisa
    ).pipe(
      map(response => response.data)
    );
  }

  onSelect(entityGuid: string) {
    this.selectedEntity = this.entityList.find( entity => this.options.guidSelector(entity) === entityGuid);
    this.entitySelected.emit(this.selectedEntity);
  }

  valueSelector(option: T) {
    return this.options.guidSelector(option);
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }
}
